import { useEffect, useMemo, useState } from 'react';
import { api } from '../../../services/api';
import MailSheet from './MailSheet';
import { C, S, btn } from './mailStyles';

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const DEFAULT_TIME = '08:00';

function timeLabel(hhmm) {
  const [h, m] = hhmm.split(':').map(Number);
  const suffix = h < 12 ? 'AM' : 'PM';
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${suffix}`;
}

const QUARTER_HOURS = Array.from({ length: 96 }, (_, i) =>
  `${String(Math.floor(i / 4)).padStart(2, '0')}:${String((i % 4) * 15).padStart(2, '0')}`);

export default function MailDigestSettingsSheet({ onClose, onSaved }) {
  const [reloadKey, setReloadKey] = useState(0);
  const [loadState, setLoadState] = useState('loading');
  const [enabled, setEnabled] = useState(false);
  const [localTime, setLocalTime] = useState(DEFAULT_TIME);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    let alive = true;
    api.getMailSettings()
      .then((res) => {
        if (!alive) return;
        setEnabled(Boolean(res?.enabled));
        setLocalTime(typeof res?.local_time === 'string' && TIME_RE.test(res.local_time) ? res.local_time : DEFAULT_TIME);
        setLoadState('ok');
      })
      .catch(() => { if (alive) setLoadState('error'); });
    return () => { alive = false; };
  }, [reloadKey]);

  const options = useMemo(
    () => (QUARTER_HOURS.includes(localTime) ? QUARTER_HOURS : [...QUARTER_HOURS, localTime].sort()),
    [localTime],
  );

  const retry = () => {
    setLoadState('loading');
    setReloadKey((k) => k + 1);
  };

  const save = async () => {
    if (saving || loadState !== 'ok') return;
    setSaving(true);
    setSaveError(null);
    try {
      await api.updateMailSettings({ enabled, local_time: localTime });
      onSaved?.({ enabled, local_time: localTime });
      onClose?.();
    } catch (err) {
      setSaveError(err?.status === 422 ? 'That delivery time isn\'t valid.' : "Couldn't save. Try again.");
      setSaving(false);
    }
  };

  const footer = (
    <>
      {saveError && <p style={S.error}>{saveError}</p>}
      <button
        type="button"
        style={btn({ ...S.btnPrimary, flex: 'none', width: '100%' }, saving || loadState !== 'ok')}
        disabled={saving || loadState !== 'ok'}
        onClick={save}
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
    </>
  );

  return (
    <MailSheet title="Daily email digest" onClose={onClose} footer={footer} testId="mail-settings-sheet">
      {loadState === 'loading' && <p style={S.sub}>Loading…</p>}
      {loadState === 'error' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
          <p style={S.error}>Couldn't load your digest settings.</p>
          <button type="button" style={S.btnLink} onClick={retry}>Retry</button>
        </div>
      )}
      {loadState === 'ok' && (
        <>
          <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
            <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>Send me a daily digest</span>
            <input
              type="checkbox"
              role="switch"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              style={{ width: 20, height: 20, accentColor: C.green, cursor: 'pointer' }}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 12, opacity: enabled ? 1 : 0.5 }}>
            <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>Delivery time</span>
            <select
              value={localTime}
              disabled={!enabled}
              onChange={(e) => setLocalTime(e.target.value)}
              style={{ ...S.input, width: 'auto', minWidth: 120 }}
            >
              {options.map((t) => <option key={t} value={t}>{timeLabel(t)}</option>)}
            </select>
          </label>
          <p style={S.sub}>Only emails that need you. Nothing on quiet days.</p>
        </>
      )}
    </MailSheet>
  );
}
